import React from 'react';

import { Box, Divider, FormField, Label, LabeledControl, Paragraph, Switch } from '@zillow/constellation-10';

import type { Section } from '../../../models';
import { SECURITY_DEPOSIT } from '../../../constants';
import YesAndNoExpansion from '../../Widgets/YesAndNoExpansion';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

function SecurityDepositAdditionalOptions({ section, setSectionValues }: Props) {
  function onNoTenUnitsOrMore(): void {
    setSectionValues({
      [SECURITY_DEPOSIT.FIELD_NAME.TEN_UNITS_OR_MORE]: false,
    });
  }

  function onYesTenUnitsOrMore(): void {
    setSectionValues({
      [SECURITY_DEPOSIT.FIELD_NAME.TEN_UNITS_OR_MORE]: true,
    });
  }

  function onSelectRentalSecurityInsurance(): void {
    setSectionValues({
      [SECURITY_DEPOSIT.FIELD_NAME.RENTAL_SECURITY_INSURANCE]: !section.getFieldValue(
        SECURITY_DEPOSIT.FIELD_NAME.RENTAL_SECURITY_INSURANCE,
      ),
    });
  }

  function onSelectMonthlyPaymentPlan(): void {
    setSectionValues({
      [SECURITY_DEPOSIT.FIELD_NAME.MONTHLY_PAYMENT_PLAN]: !section.getFieldValue(
        SECURITY_DEPOSIT.FIELD_NAME.MONTHLY_PAYMENT_PLAN,
      ),
    });
  }

  function onSelectReducedSecurityDeposit(): void {
    setSectionValues({
      [SECURITY_DEPOSIT.FIELD_NAME.REDUCED_SECURITY_DEPOSIT]: !section.getFieldValue(
        SECURITY_DEPOSIT.FIELD_NAME.REDUCED_SECURITY_DEPOSIT,
      ),
    });
  }

  return (
    <>
      <FormField
        required
        label={<Label>Do you own and/or control more than 10 rental units?</Label>}
        control={
          <YesAndNoExpansion
            yesHandler={onYesTenUnitsOrMore}
            noHandler={onNoTenUnitsOrMore}
            yesActive={section.getFieldValue(SECURITY_DEPOSIT.FIELD_NAME.TEN_UNITS_OR_MORE)}
          >
            <Box css={{ marginTop: 'layout.tight' }}>
              <Label>In lieu of a security deposit, which options will you allow for the property?</Label>
              <LabeledControl
                labelPosition="left"
                fluid
                size="lg"
                label={
                  <Label>
                    <Paragraph textStyle="body-lg-bold">Rental security insurance</Paragraph>
                    <Paragraph>Criteria here tbd</Paragraph>
                  </Label>
                }
                control={
                  <Switch
                    onChange={onSelectRentalSecurityInsurance}
                    checked={section.getFieldValue(SECURITY_DEPOSIT.FIELD_NAME.RENTAL_SECURITY_INSURANCE) ?? false}
                  />
                }
              />
              <Divider css={{ marginTop: 'layout.tighter', marginBottom: 'layout.tighter' }} />
              <LabeledControl
                labelPosition="left"
                fluid
                size="lg"
                label={
                  <Label>
                    <Paragraph textStyle="body-lg-bold">Monthly payments of security deposit</Paragraph>
                    <Paragraph>Series of no less of 6 monthly equal payment installment plan</Paragraph>
                  </Label>
                }
                control={
                  <Switch
                    onChange={onSelectMonthlyPaymentPlan}
                    checked={section.getFieldValue(SECURITY_DEPOSIT.FIELD_NAME.MONTHLY_PAYMENT_PLAN) ?? false}
                  />
                }
              />
              <Divider css={{ marginTop: 'layout.tighter', marginBottom: 'layout.tighter' }} />
              <LabeledControl
                labelPosition="left"
                fluid
                size="lg"
                label={
                  <Label>
                    <Paragraph textStyle="body-lg-bold">Reduced security deposit</Paragraph>
                    <Paragraph>Shall be no more than 50% of the base rent of the unit</Paragraph>
                  </Label>
                }
                control={
                  <Switch
                    onChange={onSelectReducedSecurityDeposit}
                    checked={section.getFieldValue(SECURITY_DEPOSIT.FIELD_NAME.REDUCED_SECURITY_DEPOSIT) ?? false}
                  />
                }
              />
            </Box>
          </YesAndNoExpansion>
        }
      />
    </>
  );
}

SecurityDepositAdditionalOptions.description =
  'Atlanta screen where security deposit options are based on whether or not the landlord owns more than 10 properties.';

export default SecurityDepositAdditionalOptions;
